import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Table,
  Typography,
  Tag,
  App,
  Spin,
  Popconfirm,
  Upload,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
  UploadOutlined,
  QrcodeOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import QRCode from 'qrcode.react';
import complianceService, { type InvoiceExchangeTransmission } from '@/services/compliance.service';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import directPurchaseInvoiceService from '@/services/directPurchaseInvoice.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import { ENTITY_TYPE_SUPPLIER_INVOICE } from '@/constants/approval-entity-types';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

function invoiceStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Draft: t('statusDraft'),
    Submitted: t('statusSubmitted'),
    Approved: t('statusApproved'),
    Posted: t('statusPosted'),
    Cancelled: t('statusCancelled'),
  };
  return map[status] ?? status;
}

function paymentStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Unpaid: t('statusUnpaid'),
    'Partially Paid': t('statusPartiallyPaid'),
    Paid: t('statusPaid'),
  };
  return map[status] ?? status;
}

const DirectPurchaseInvoiceDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [uaeEinvoiceLoading, setUaeEinvoiceLoading] = useState(false);
  const [uaeQrModalVisible, setUaeQrModalVisible] = useState(false);

  const { data: inv, isLoading } = useQuery({
    queryKey: ['direct-purchase-invoice', id],
    queryFn: () => directPurchaseInvoiceService.getById(id!),
    enabled: !!id,
  });

  const { data: uaeTransmissions = [], refetch: refetchUaeTransmissions } = useQuery({
    queryKey: ['uae-einvoice-transmissions', 'DirectPurchaseInvoice', id],
    queryFn: () =>
      id
        ? complianceService.listTransmissions({
            document_type: 'DirectPurchaseInvoice',
            document_id: id,
          })
        : Promise.resolve([] as InvoiceExchangeTransmission[]),
    enabled: !!id && !!inv?.gl_posted,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ['direct-purchase-invoice-payments', id],
    queryFn: () => directPurchaseInvoiceService.listPayments(id!),
    enabled: !!id && !!inv?.gl_posted,
  });

  const latestUaeTransmission = React.useMemo(
    () =>
      [...uaeTransmissions].sort(
        (a, b) => new Date(b.request_at).getTime() - new Date(a.request_at).getTime()
      )[0],
    [uaeTransmissions]
  );

  const uaeQrPayload = latestUaeTransmission?.qr_payload ?? '';

  const runAction = async (key: string, fn: () => Promise<unknown>) => {
    setActionLoading(key);
    try {
      await fn();
      message.success(tc('done'));
      queryClient.invalidateQueries({ queryKey: ['direct-purchase-invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['direct-purchase-invoices'] });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || tc('actionFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading || !inv) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const showUniversalApproval = !!inv.approval_instance_id;
  const canEdit = ['Draft', 'Submitted'].includes(inv.invoice_status);
  const canPost =
    !inv.gl_posted && (inv.invoice_status === 'Approved' || inv.approval_status === 'Approved');
  const canUnpost = !!inv.gl_posted && Number(inv.paid_amount ?? 0) === 0;
  const canPay = !!inv.gl_posted && inv.invoice_status !== 'Cancelled' && Number(inv.balance_due ?? 0) > 0.01;
  const canDeleteAttachment = !inv.gl_posted && inv.invoice_status !== 'Cancelled';

  const openAttachment = async (att: { id: string; file_name: string; mime_type?: string | null }, mode: 'view' | 'download') => {
    const viewer = mode === 'view' ? window.open('', '_blank') : null;
    try {
      const raw = await directPurchaseInvoiceService.getAttachmentBlob(att.id, mode === 'view');
      const blob = att.mime_type ? new Blob([raw], { type: att.mime_type }) : raw;
      const url = URL.createObjectURL(blob);
      if (mode === 'view') {
        if (viewer) viewer.location.href = url;
        else window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = att.file_name;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      viewer?.close();
      message.error(e?.response?.status === 404 ? t('fileNotFoundOnServer') : t('couldNotOpenAttachment'));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/direct-purchase-invoices')}>
          {tc('back')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {inv.invoice_number}
        </Title>
        <Tag color={statusColors[inv.invoice_status] ?? 'default'}>{invoiceStatusLabel(inv.invoice_status, t)}</Tag>
        {inv.gl_posted && <Tag color="cyan">{t('glPosted')}</Tag>}
        <Tag color="blue">{t('directExpense')}</Tag>
        {canEdit && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/fin/direct-purchase-invoices/${id}/edit`)}>
            {tc('edit')}
          </Button>
        )}
        {inv.invoice_status === 'Draft' && (
          <Button loading={actionLoading === 'submit'} onClick={() => runAction('submit', () => directPurchaseInvoiceService.submit(id!))}>
            {tc('submit')}
          </Button>
        )}
        {inv.invoice_status === 'Submitted' && !showUniversalApproval && (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={actionLoading === 'approve'}
            onClick={() => runAction('approve', () => directPurchaseInvoiceService.approve(id!))}
          >
            {tc('approve')}
          </Button>
        )}
        {canPost && (
          <Popconfirm
            title={t('postToGlConfirmTitle')}
            description={t('postToGlConfirmDesc')}
            onConfirm={() => runAction('post', () => directPurchaseInvoiceService.post(id!))}
            okText={tc('post')}
          >
            <Button type="primary" icon={<CheckCircleOutlined />} loading={actionLoading === 'post'}>
              {t('postToGl')}
            </Button>
          </Popconfirm>
        )}
        {canPay && (
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => navigate(`/fin/payment-vouchers/new?supplierInvoiceId=${id}`)}
          >
            {t('makePayment')}
          </Button>
        )}
        {canUnpost && (
          <Popconfirm
            title={t('unpostConfirmTitle')}
            description={t('unpostConfirmDesc')}
            onConfirm={() => runAction('unpost', () => directPurchaseInvoiceService.unpost(id!))}
            okText={t('unpost')}
          >
            <Button loading={actionLoading === 'unpost'}>{t('unpost')}</Button>
          </Popconfirm>
        )}
        {!inv.gl_posted && inv.invoice_status !== 'Cancelled' && (
          <Popconfirm title={t('cancelInvoice')} onConfirm={() => runAction('cancel', () => directPurchaseInvoiceService.cancel(id!))}>
            <Button danger icon={<StopOutlined />} loading={actionLoading === 'cancel'}>
              {tc('cancel')}
            </Button>
          </Popconfirm>
        )}
        <ReportDocumentActions
          reportKey="direct-purchase-invoice"
          params={id ? { id } : null}
          documentId={id}
          sendPermissions={['direct_purchase_invoice.create', 'direct_purchase_invoice.edit']}
          emailLabel={t('emailSupplier')}
        />
        {(inv.gl_posted || inv.journal_voucher_id) && (
          <ReportDocumentActions
            reportKey="accounting-voucher"
            params={id ? { source_type: 'direct_purchase_invoice', source_id: id } : null}
            documentId={id}
            sendPermissions={['direct_purchase_invoice.create']}
            emailLabel={t('emailGl')}
          />
        )}
        {inv.gl_posted && inv.invoice_status !== 'Cancelled' && (
          <>
            <Button
              icon={<QrcodeOutlined />}
              loading={uaeEinvoiceLoading}
              onClick={async () => {
                if (!id) return;
                setUaeEinvoiceLoading(true);
                try {
                  await complianceService.generateUaeEInvoice(id, 'DirectPurchaseInvoice');
                  message.success(t('uaeEinvoiceArchived'));
                  refetchUaeTransmissions();
                } catch (e: unknown) {
                  const err = e as { response?: { data?: { message?: string } }; message?: string };
                  message.error(err?.response?.data?.message || err?.message || t('uaeEinvoiceFailed'));
                } finally {
                  setUaeEinvoiceLoading(false);
                }
              }}
            >
              {t('generateUaeEInvoice')}
            </Button>
            {uaeQrPayload ? (
              <Button icon={<QrcodeOutlined />} onClick={() => setUaeQrModalVisible(true)}>
                {t('viewQr')}
              </Button>
            ) : null}
          </>
        )}
        {inv.journal_voucher_id && (
          <Button type="link" onClick={() => navigate(`/fin/gl/journals?voucher=${inv.journal_voucher_id}`)}>
            {inv.journalVoucher?.voucher_number
              ? t('jvNumber', { number: inv.journalVoucher.voucher_number })
              : t('viewJournal')}
          </Button>
        )}
      </Space>

      {(showUniversalApproval || inv.invoice_status === 'Submitted') && inv.invoice_status !== 'Cancelled' && (
        <Card style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_SUPPLIER_INVOICE}
            entityId={inv.id}
            onApproveViaDocument={async (remarks) => {
              await directPurchaseInvoiceService.approve(id!, remarks);
            }}
            onRejectViaDocument={async (payload) => {
              await directPurchaseInvoiceService.rejectApproval(id!, payload);
            }}
            extraInvalidateQueryKeys={[['direct-purchase-invoice', id], ['direct-purchase-invoices']]}
          />
        </Card>
      )}

      <Card title={t('header')} style={{ marginBottom: 16 }}>
        <Descriptions column={3} size="small">
          <Descriptions.Item label={t('supplier')}>{inv.supplier?.supplier_name}</Descriptions.Item>
          <Descriptions.Item label={t('supplierInvHash')}>{inv.supplier_invoice_number}</Descriptions.Item>
          <Descriptions.Item label={t('supplierInvDate')}>{dayjs(inv.supplier_invoice_date).format('DD MMM YYYY')}</Descriptions.Item>
          <Descriptions.Item label={t('due')}>{dayjs(inv.due_date).format('DD MMM YYYY')}</Descriptions.Item>
          <Descriptions.Item label={t('apCreditAccountShort')}>
            {inv.debitAccount ? `${inv.debitAccount.account_code} - ${inv.debitAccount.account_name}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('discount')}>{formatCurrency(inv.discount_amount)}</Descriptions.Item>
          <Descriptions.Item label={t('freightOtherRounding')}>
            {formatCurrency(
              Number(inv.freight_charges ?? 0) +
                Number(inv.other_charges ?? 0) +
                Number(inv.rounding_difference ?? 0)
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('department')}>{inv.department ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('paymentStatus')}>{paymentStatusLabel(inv.payment_status, t)}</Descriptions.Item>
          <Descriptions.Item label={t('subtotal')}>{formatCurrency(inv.subtotal)}</Descriptions.Item>
          <Descriptions.Item label={t('vat')}>{formatCurrency(inv.vat_amount)}</Descriptions.Item>
          <Descriptions.Item label={t('grandTotal')}>{formatCurrency(inv.grand_total)}</Descriptions.Item>
          <Descriptions.Item label={t('paid')}>{formatCurrency(inv.paid_amount)}</Descriptions.Item>
          <Descriptions.Item label={t('outstanding')}>{formatCurrency(inv.balance_due)}</Descriptions.Item>
          {payments.some((p) => p.payment_voucher_id) && (
            <Descriptions.Item label={t('paymentVoucher')}>
              <Space size={4} wrap>
                {[...new Map(payments.filter((p) => p.payment_voucher_id).map((p) => [p.payment_voucher_id, p])).values()].map((p) => (
                  <Typography.Link key={p.payment_voucher_id} onClick={() => navigate(`/fin/payment-vouchers/${p.payment_voucher_id}`)}>
                    {p.reference}
                  </Typography.Link>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          {inv.journalVoucher?.voucher_number && (
            <Descriptions.Item label={t('journalVoucher')}>{inv.journalVoucher.voucher_number}</Descriptions.Item>
          )}
          {inv.notes && (
            <Descriptions.Item label={t('remarks')} span={3}>
              {inv.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {payments.length > 0 && (
        <Card title={t('payments')} style={{ marginBottom: 16 }} size="small">
          <Table
            size="small"
            pagination={false}
            rowKey={(p) => `${p.kind}-${p.payment_voucher_id ?? p.debit_note_id}-${p.date}-${p.amount}`}
            dataSource={payments}
            columns={[
              { title: t('type'), dataIndex: 'kind', width: 150 },
              {
                title: t('reference'),
                dataIndex: 'reference',
                render: (v: string, p) => {
                  const target = p.payment_voucher_id
                    ? `/fin/payment-vouchers/${p.payment_voucher_id}`
                    : p.debit_note_id
                      ? `/fin/debit-notes/${p.debit_note_id}`
                      : null;
                  return target ? <Typography.Link onClick={() => navigate(target)}>{v}</Typography.Link> : v;
                },
              },
              {
                title: t('settledVia'),
                key: 'settlement',
                render: (_: unknown, p) =>
                  p.settlement_payment_voucher_id && p.settlement_payment_voucher_id !== p.payment_voucher_id ? (
                    <Typography.Link onClick={() => navigate(`/fin/payment-vouchers/${p.settlement_payment_voucher_id}`)}>
                      {p.settlement_voucher_number}
                    </Typography.Link>
                  ) : (
                    '—'
                  ),
              },
              { title: t('date'), dataIndex: 'date', width: 120, render: (v: string | null) => (v ? dayjs(v).format('DD MMM YYYY') : '—') },
              { title: t('mode'), dataIndex: 'payment_mode', width: 110, render: (v: string | null) => v ?? '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string | null) =>
                  v ? <Tag color={v === 'Posted' ? 'green' : 'default'}>{invoiceStatusLabel(v, t)}</Tag> : '—',
              },
              { title: t('amount'), dataIndex: 'amount', align: 'right', width: 140, render: (v: number) => formatCurrency(v) },
            ]}
          />
        </Card>
      )}

      {inv.gl_posted && (
        <Card title={t('uaeEinvoiceInbound')} style={{ marginBottom: 16 }} size="small">
          {latestUaeTransmission ? (
            <Descriptions column={2} size="small">
              <Descriptions.Item label={t('status')}>
                <Tag color={latestUaeTransmission.status === 'Cleared' ? 'green' : 'default'}>
                  {latestUaeTransmission.status}
                </Tag>
              </Descriptions.Item>
              {latestUaeTransmission.clearance_number && (
                <Descriptions.Item label={t('clearanceNumber')}>{latestUaeTransmission.clearance_number}</Descriptions.Item>
              )}
              {latestUaeTransmission.exchange_uuid && (
                <Descriptions.Item label={t('uuid')} span={2}>
                  {latestUaeTransmission.exchange_uuid}
                </Descriptions.Item>
              )}
            </Descriptions>
          ) : (
            <Text type="secondary">{t('uaeEinvoiceGenerateHint')}</Text>
          )}
        </Card>
      )}

      <Card title={t('expenseAllocationLines')} style={{ marginBottom: 16 }}>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={inv.lines ?? []}
          columns={[
            {
              title: t('expenseAccount'),
              key: 'exp',
              render: (_, r) =>
                r.expenseAccount ? `${r.expenseAccount.account_code} - ${r.expenseAccount.account_name}` : r.expense_account_id,
            },
            {
              title: t('description'),
              dataIndex: 'item_description',
              render: (desc: string, r) => (
                <>
                  <div>{desc}</div>
                  <LineSpecificationsText value={r.specifications} />
                </>
              ),
            },
            {
              title: t('treatment'),
              key: 'treatment',
              render: (_, r) => {
                if (r.expense_treatment !== 'PREPAID_EXPENSE') return t('expense');
                return r.recognition_contract_id ? (
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/fin/prepaid/contracts/${r.recognition_contract_id}`)}>
                    {t('prepaidViewContract')}
                  </Button>
                ) : (
                  <Tag color="blue">{t('prepaidContractOnPost')}</Tag>
                );
              },
            },
            {
              title: t('costCenter'),
              key: 'project',
              render: (_, r) => {
                const p = r.project;
                if (p?.project_name) return p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name;
                return r.project_id ?? '—';
              },
            },
            {
              title: t('vatCode'),
              key: 'vat',
              render: (_, r) => {
                const vc = r.vatConfiguration;
                if (!vc) return '—';
                return vc.description ? `${vc.description} (${vc.vat_rate}%)` : `${vc.vat_type} ${vc.vat_rate}%`;
              },
            },
            {
              title: t('inputVatGl'),
              key: 'vat_gl',
              render: (_, r) => {
                const gl = r.inputVatAccount;
                if (gl) return `${gl.account_code} - ${gl.account_name}`;
                return '—';
              },
            },
            { title: t('net'), dataIndex: 'line_subtotal', align: 'right', render: (v) => formatCurrency(Number(v)) },
            { title: t('tax'), dataIndex: 'tax_amount', align: 'right', render: (v) => formatCurrency(Number(v)) },
            { title: t('lineTotal'), dataIndex: 'amount', align: 'right', render: (v) => formatCurrency(Number(v)) },
          ]}
        />
      </Card>

      <Card title={tc('attachments')}>
        <Upload
          beforeUpload={(file) => {
            directPurchaseInvoiceService
              .uploadAttachment(id!, file)
              .then(() => {
                message.success(t('uploaded'));
                queryClient.invalidateQueries({ queryKey: ['direct-purchase-invoice', id] });
              })
              .catch((e: any) => message.error(e?.response?.data?.message || t('uploadFailed')));
            return false;
          }}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>{t('uploadFile')}</Button>
        </Upload>
        {(inv.attachments?.length ?? 0) > 0 ? (
          <Table
            style={{ marginTop: 12 }}
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={inv.attachments}
            columns={[
              {
                title: t('file'),
                dataIndex: 'file_name',
                render: (v: string, a) => <Typography.Link onClick={() => openAttachment(a, 'view')}>{v}</Typography.Link>,
              },
              {
                title: t('uploaded'),
                dataIndex: 'created_at',
                width: 170,
                render: (v: string) => dayjs(v).format('DD MMM YYYY HH:mm'),
              },
              {
                title: '',
                key: 'actions',
                width: 260,
                render: (_: unknown, a) => (
                  <Space size="small">
                    <Button size="small" icon={<EyeOutlined />} onClick={() => openAttachment(a, 'view')}>
                      {tc('view')}
                    </Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => openAttachment(a, 'download')}>
                      {t('download')}
                    </Button>
                    {canDeleteAttachment && (
                      <Popconfirm
                        title={t('deleteAttachmentConfirm')}
                        onConfirm={() =>
                          directPurchaseInvoiceService
                            .deleteAttachment(a.id)
                            .then(() => {
                              message.success(t('attachmentDeleted'));
                              queryClient.invalidateQueries({ queryKey: ['direct-purchase-invoice', id] });
                            })
                            .catch((e: any) => message.error(e?.response?.data?.message || t('deleteFailed')))
                        }
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        ) : (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {t('noAttachments')}
          </Text>
        )}
      </Card>

      <Modal
        title={t('uaeEinvoiceQr')}
        open={uaeQrModalVisible}
        onCancel={() => setUaeQrModalVisible(false)}
        footer={null}
        centered
      >
        {uaeQrPayload ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <QRCode value={uaeQrPayload} size={200} level="H" />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default DirectPurchaseInvoiceDetailPage;
